import { useInformationList } from "@/api/services/main/information";
import { NotificationList } from "@/components/commons/notification/NotificationList";
import { InfiniteScroll } from "@/modules/irohana-components/elements/InfiniteScroll";
import { LoadingArea } from "@/modules/irohana-components/elements/Loading/Loading";
import { Tab } from "@/components/elements/Tab";
import { isNotificationTab, NOTIFICATION_TAB, NOTIFICATION_TAB_LABEL, NOTIFICATION_TYPE, TNotificationTab } from "@/features/dashboard/constants/notificationTab";
import { AppRoutes } from "@/routes/config";
import { state } from "@/theme/colors";
import { fonts } from "@/theme/typography";
import { Divider, styled } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

// Max items shown per tab when rendered as a dashboard preview (お知らせ section).
const PREVIEW_LIMIT = 10;

export type TNotificationProps = {
  isSingleLine?: boolean;
  isHideContent?: boolean;
  // Preview mode: limit each tab to PREVIEW_LIMIT items + "もっと見る" link (dashboard ホーム).
  isPreview?: boolean;
};

export const Notification: FC<TNotificationProps> = ({ isSingleLine = false, isHideContent = false, isPreview = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Full-page mode (お知らせ一覧): restore the tab carried over from the dashboard preview via ?tab=.
  const [tab, setTab] = useState<TNotificationTab>(() => {
    const tabParam = searchParams.get("tab");
    return !isPreview && tabParam && isNotificationTab(tabParam) ? tabParam : NOTIFICATION_TAB.ALL;
  });

  const handleChangeTab = (nextTab: TNotificationTab) => {
    setTab(nextTab);
    // Keep the URL in sync so the selected tab survives reload/back on the full page (not the dashboard preview).
    if (!isPreview) {
      setSearchParams(
        (prev) => {
          prev.set("tab", nextTab);
          return prev;
        },
        { replace: true },
      );
    }
  };
  const perPage = isPreview ? PREVIEW_LIMIT : undefined;
  const {
    dataFormatter: informationListData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    total,
  } = useInformationList(
    {
      types: tab === NOTIFICATION_TAB.ALL ? Object.values(NOTIFICATION_TYPE) : tab,
    },
    perPage,
    // Keep the preview's paging state out of the list/modal cache entry (same perPage would collide).
    isPreview ? "preview" : undefined,
  );

  const notificationListData = useMemo(
    () =>
      informationListData.map((notification) => ({
        id: notification.id,
        description: notification.content,
        time: notification.startDate,
        title: notification.title,
        type: notification.type,
        filePath: notification.filePath,
      })),
    [informationListData],
  );

  // Preview shows at most PREVIEW_LIMIT items regardless of how many pages sit in the cache.
  const previewListData = useMemo(() => notificationListData.slice(0, PREVIEW_LIMIT), [notificationListData]);

  return (
    <Tab
      value={tab}
      onChange={(_, tab) => handleChangeTab(tab as TNotificationTab)}
      options={Object.values(NOTIFICATION_TAB).map((tab) => ({ label: NOTIFICATION_TAB_LABEL[tab], value: tab }))}
    >
      {!informationListData || isLoading ? (
        <LoadingArea height={100} />
      ) : isPreview ? (
        <>
          <NotificationList data={previewListData} isSingleLine={isSingleLine} isHideContent={isHideContent} />
          {total > previewListData.length && (
            <>
              <Divider />
              <ViewMoreLink to={tab === NOTIFICATION_TAB.ALL ? AppRoutes.information : `${AppRoutes.information}?tab=${tab}`}>
                もっと見る
              </ViewMoreLink>
            </>
          )}
        </>
      ) : (
        <InfiniteScroll loadMore={fetchNextPage} hasMore={hasNextPage}>
          <NotificationList data={notificationListData} isSingleLine={isSingleLine} isHideContent={isHideContent} />
        </InfiniteScroll>
      )}
    </Tab>
  );
};

const ViewMoreLink = styled(Link)`
  display: inline-block;
  width: fit-content;
  cursor: pointer;
  padding: 12px 0 16px 12px;
  font-family: ${fonts};
  font-weight: 400;
  font-size: 14px;
  line-height: 170%;
  text-decoration: none;

  &,
  &:hover,
  &:visited,
  &:active {
    color: ${state.link_1};
  }

  &:hover {
    text-decoration: underline;
  }
`;
